import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import useMediaQuery from '@mui/material/useMediaQuery';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import { InternalLink } from '@components/Link';
import { ProfileType, ProfileName } from '@constants/integrations';
import { RoutePath } from '@constants/routes';
import { SurveysIcon, ReviewsIcon } from 'assets/images/sidebar';
import { UserIcon, CustomEventIcon, CouponRedeemedIcon } from 'assets/images/icons';
import { CoverManagerIcon, VTEXIcon } from 'assets/images/integrations';
import { useTranslation } from '@hooks';
import { getChannelIcon } from '@helpers/channels';
import { pretifyObject } from '@helpers/pretify-object';
import { contactsActions } from '@actions';
import { ActivityWrapper, IconWrapper, CustomEventDataRaw } from './styles';

const ACTIVITY = {
    MessageSent: 0,
    SurveyResponded: 1,
    SurveySent: 2,
    SurveyReminderSent: 3,
    ConversationStarted: 4,
    ConversationClosed: 5,
    ConversationReopened: 6,
    ContactCreated: 7,
    ReviewLeft: 8,
    Integration: 9,
    CustomEvent: 10,
    CouponRedeemed: 11,
};

const Activity = ({ contact, isSmallScreen, resizable, setActiveConversation = null }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const dispatch = useDispatch();

    const onClickInboxActivity = () => {
        if (isMobile) {
            dispatch(contactsActions.setContactOpen(false));
            // INFO: if mobile, close contact drawer and inbox to return to the list of conversations
            if (setActiveConversation) {
                setActiveConversation(null);
            }
        }
    };

    const getIntegrationEventIcon = integrationType => {
        switch (integrationType) {
            case ProfileName.CoverManager:
                return <CoverManagerIcon height={16} width={16} style={{ transform: 'translateY(0px)' }} />;
            case ProfileName.VTEX:
                return <VTEXIcon height={17} width={17} style={{ transform: 'translateY(0px)' }} />;
            default:
                return null;
        }
    };

    const getActivityIcon = item => {
        switch (item?.type) {
            case ACTIVITY.SurveyResponded:
                return (
                    <IconWrapper>
                        <SurveysIcon />
                    </IconWrapper>
                );
            case ACTIVITY.ReviewLeft:
                return (
                    <IconWrapper>
                        <ReviewsIcon />
                    </IconWrapper>
                );
            case ACTIVITY.ContactCreated:
                return (
                    <IconWrapper>
                        <UserIcon />
                    </IconWrapper>
                );
            case ACTIVITY.Integration:
                return getIntegrationEventIcon(item?.integrationType);
            case ACTIVITY.CustomEvent:
                return <CustomEventIcon style={{ transform: 'translateY(0px)' }} />;
            case ACTIVITY.CouponRedeemed:
                return <CouponRedeemedIcon style={{ transform: 'translateY(0px)' }} />;
            default:
                return getChannelIcon(item?.channelType);
        }
    };

    return contact?.activities?.map(item => (
        <ActivityWrapper key={item.id}>
            {getActivityIcon(item)}
            {item?.type === ACTIVITY.MessageSent && (
                <div>
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityMessageSent', {
                            channel: item?.channelName,
                        })}
                    </Typography>
                    <Tooltip title={item?.message} placement="top">
                        <span style={{ cursor: 'pointer' }}>
                            <Typography variant="caption" sx={{ color: '#1F4C5C', display: 'block' }}>
                                <ErrorOutlineRoundedIcon sx={{ fontSize: 12, marginRight: '4px' }} />
                                {t('buttons.viewMore')}
                            </Typography>
                        </span>
                    </Tooltip>
                </div>
            )}
            {item?.type === ACTIVITY.SurveyResponded && (
                <InternalLink to={`${RoutePath.SurveysResponses}?feedbackId=${item.feedbackId}`} underline="hover">
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activitySurveyResponded', {
                            template: item?.surveyName,
                            rating: item?.rating,
                        })}
                    </Typography>
                </InternalLink>
            )}
            {item?.type === ACTIVITY.SurveySent && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('ContactsPage.activitySurveySent', { template: item?.surveyName })}
                </Typography>
            )}
            {item?.type === ACTIVITY.SurveyReminderSent && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('ContactsPage.activitySurveyReminder', { template: item?.surveyName })}
                </Typography>
            )}
            {item?.type === ACTIVITY.ConversationStarted &&
                (item?.conversationId ? (
                    <InternalLink
                        to={`${RoutePath.MessagingInbox}?conversationId=${item?.conversationId}`}
                        onClick={onClickInboxActivity}
                        underline="hover"
                    >
                        <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                            {t('ContactsPage.activityConversationStarted')}
                        </Typography>
                    </InternalLink>
                ) : (
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityConversationStarted')}
                    </Typography>
                ))}
            {item?.type === ACTIVITY.ConversationClosed &&
                (item?.conversationId ? (
                    <InternalLink
                        to={`${RoutePath.MessagingInbox}?conversationId=${item?.conversationId}`}
                        onClick={onClickInboxActivity}
                        underline="hover"
                    >
                        <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                            {t('ContactsPage.activityConversationClosed', {
                                name: item?.userName || item?.agentName,
                            })}
                        </Typography>
                    </InternalLink>
                ) : (
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityConversationClosed', {
                            name: item?.userName || item?.agentName,
                        })}
                    </Typography>
                ))}
            {item?.type === ACTIVITY.ConversationReopened &&
                (item?.conversationId ? (
                    <InternalLink
                        to={`${RoutePath.MessagingInbox}?conversationId=${item?.conversationId}`}
                        onClick={onClickInboxActivity}
                        underline="hover"
                    >
                        <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                            {t('ContactsPage.activityConversationReopened', {
                                name: item?.userName,
                            })}
                        </Typography>
                    </InternalLink>
                ) : (
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityConversationReopened', { name: item?.userName })}
                    </Typography>
                ))}
            {item?.type === ACTIVITY.ContactCreated && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('common.contactCreated')}
                </Typography>
            )}
            {item?.type === ACTIVITY.ReviewLeft && (
                <InternalLink to={`${RoutePath.ReviewsManage}?reviewId=${item.reviewId}`} underline="hover">
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityReviewLeft', {
                            reviewType: ProfileType[item?.reviewType],
                        })}
                    </Typography>
                </InternalLink>
            )}
            {item?.type === ACTIVITY.Integration && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('ContactsPage.activityIntegrationEvent', {
                        integration: ProfileType[item?.integrationType],
                    })}
                </Typography>
            )}
            {item?.type === ACTIVITY.CustomEvent && (
                <div>
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('common.customEvent')}: {item?.customEventType}
                    </Typography>
                    <Tooltip
                        title={
                            <CustomEventDataRaw>
                                {pretifyObject(JSON.parse(item?.customEventDataRaw || '{}'))}
                            </CustomEventDataRaw>
                        }
                        placement="top"
                    >
                        <span style={{ cursor: 'pointer' }}>
                            <Typography variant="caption" sx={{ color: '#1F4C5C', display: 'block' }}>
                                <ErrorOutlineRoundedIcon sx={{ fontSize: 12, marginRight: '4px' }} />
                                {t('buttons.viewMore')}
                            </Typography>
                        </span>
                    </Tooltip>
                </div>
            )}
            {item?.type === ACTIVITY.CouponRedeemed && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('CouponsPage.redeemed', { title: item?.couponTitle })}
                </Typography>
            )}
            <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.38)', display: 'block', textAlign: 'end' }}>
                {format(new Date(item?.createdAt), 'dd/MM/yy HH:mm')}
            </Typography>
        </ActivityWrapper>
    ));
};

export default Activity;
